//This is a temp placeholder for the CMS Image component
import React, { memo } from 'react';
import { css, useTheme } from '@emotion/react';
import type { ImageEntryType } from 'tsconfig/types.d';
import Image from 'next/image';
import { inspectorMode, useScreenLayout } from 'common-ui';
import { Link } from '../../Link/Link';
import nextImageLoaderFile from '../../../utils/nextImageUrl';

export type CmsImageProps = {
  contentItem?: ImageEntryType;
  //image defaults to filling the width of its container (while mainting aspect ratio)
  disableAutoSize?: boolean;
  isNextImg?: boolean;
  width?: number;
  height?: number;
  sizes?: string;
  fill?: boolean;
  blurDataURL?: string;
  placeholder?: 'empty' | 'blur' | `data:image/${string}`;
  dlClickEventHandler?: (data: string) => void;
  bannerTitle?: string; // Banner Component Title for DL event purposes
};

export const CmsImage = memo((props: CmsImageProps) => {
  const {
    disableAutoSize,
    contentItem,
    isNextImg,
    height,
    width,
    sizes,
    fill = false,
    blurDataURL,
    placeholder = 'empty',
    dlClickEventHandler,
    bannerTitle = '',
  } = props;

  const theme = useTheme();
  const { isNotDesktop } = useScreenLayout();
  const isMobileViewPort = typeof window !== 'undefined' && isNotDesktop;

  if (!contentItem) {
    return null;
  }
  const { largeDeviceImage, smallDeviceImage, altText, url = '', entryTitle } = contentItem.fields;
  const normalizedAltText = typeof altText === 'string' ? altText.trim() : '';
  const imgAltText = normalizedAltText || entryTitle || bannerTitle;
  const imgAltTextForEvent = imgAltText || 'not set';

  // Return null if no image URL is available
  const largeImageUrl = largeDeviceImage?.fields?.file?.url;
  const smallImageUrl = smallDeviceImage?.fields?.file?.url;

  if (!largeImageUrl && !smallImageUrl) {
    return null;
  }

  const imgContainStyle = css`
    max-width: 100%;
    height: auto;
    object-fit: contain;
    position: relative !important;
  `;

  const intrinsicWidth = largeDeviceImage?.fields?.file?.details?.image?.width;
  const intrinsicHeight = largeDeviceImage?.fields?.file?.details?.image?.height;

  const mediaMd = theme.breakpoints.md ? theme.breakpoints.md.replace('@media', '') : '';

  let conditionalImageProps;

  if (fill) {
    conditionalImageProps = { fill };
  } else if (disableAutoSize && width) {
    conditionalImageProps = { width, height };
  } else {
    conditionalImageProps = { width: intrinsicWidth, height: intrinsicHeight, sizes };
  }

  const mobileImage = smallImageUrl || largeImageUrl;

  let widthValue;

  if (fill) {
    widthValue = '100%';
  } else if (disableAutoSize && width) {
    widthValue = width;
  } else {
    widthValue = intrinsicWidth;
  }

  const findSectionTitle = (element: HTMLElement): string | null => {
    const { parentElement } = element;
    const heading = parentElement?.parentElement?.querySelector('h2');
    return heading ? heading.innerText : null;
  };

  const linkClickEvent = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const sectionTitle = findSectionTitle(e.currentTarget);
    const cardTitle = (bannerTitle || sectionTitle || entryTitle || '').trim();
    dlClickEventHandler?.(`${imgAltTextForEvent}:image:${cardTitle}:${url || ''}`);
  };

  // When smallDeviceImage exists, always use picture element for proper responsive loading
  // This ensures mobile devices load the correct image via media queries, not client-side JS
  const useNextImage = isNextImg && !smallDeviceImage;

  let cmsImageContent = useNextImage ? (
    <Image
      src={largeImageUrl || ''}
      alt={imgAltText}
      className="banner-img"
      css={imgContainStyle}
      blurDataURL={blurDataURL}
      placeholder={placeholder}
      data-testid="next-image"
      {...conditionalImageProps}
      {...inspectorMode(contentItem?.sys?.id, 'largeDeviceImage')}
    />
  ) : (
    <picture {...inspectorMode(contentItem?.sys?.id, (isMobileViewPort && 'smallDeviceImage') || 'largeDeviceImage')}>
      {largeImageUrl && <source media={mediaMd} srcSet={nextImageLoaderFile({ src: largeImageUrl })} />}
      <img
        width={widthValue}
        loading="lazy"
        src={nextImageLoaderFile({ src: mobileImage })}
        alt={imgAltText}
        className="banner-img"
        css={imgContainStyle}
        data-testid="cms-image"
      />
    </picture>
  );
  if (url) {
    cmsImageContent = (
      <Link onClick={linkClickEvent} href={url}>
        {cmsImageContent}
      </Link>
    );
  }

  return (
    <div data-testid="widget-CmsImage" className="cms-image-container">
      {cmsImageContent}
    </div>
  );
});
